/**
 * MarkdownContent Component
 *
 * Renders rich text content as formatted HTML.
 * Handles both HTML content (from WordPress WYSIWYG/ACF fields)
 * and Markdown content with automatic detection.
 *
 * Features:
 * - Detects HTML vs Markdown content automatically
 * - HTML content: sanitized with DOMPurify for XSS protection
 * - Markdown content: parsed with react-markdown
 * - Supports Tailwind prose styling for consistent appearance
 * - Handles empty/null content gracefully
 *
 * Bug fixes: #4 (HTML not rendering in Method Modal), #6 (PDFs)
 *
 * @component
 */

import React, { forwardRef, useMemo } from 'react';
import DOMPurify from 'dompurify';
import ReactMarkdown from 'react-markdown';

export interface MarkdownContentProps {
  /** The rich text content to render (HTML or Markdown) */
  content: string | null | undefined;
  /** Additional CSS classes to apply to the container */
  className?: string;
  /** Test ID for automated testing */
  testId?: string;
}

/**
 * Detects whether a string contains HTML tags.
 * Checks for common HTML patterns from WordPress WYSIWYG editors.
 *
 * Returns true if the content appears to be HTML (contains tags like
 * <p>, <br>, <strong>, <em>, <h1>-<h6>, <ul>, <ol>, <li>, <a>, <div>, <span>).
 */
function containsHtmlTags(text: string): boolean {
  // Match common HTML tags produced by WordPress WYSIWYG editors
  const htmlTagPattern = /<(?:p|br|strong|b|em|i|h[1-6]|ul|ol|li|a|div|span|blockquote|table|tr|td|th|thead|tbody|img|hr|pre|code|sup|sub|del|ins|figure|figcaption)[\s>/]/i;
  return htmlTagPattern.test(text);
}

/**
 * Sanitizes HTML content using DOMPurify.
 * Allows safe formatting tags while stripping dangerous content.
 */
function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      // Text formatting
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'del', 'ins', 'sup', 'sub',
      // Headings
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      // Lists
      'ul', 'ol', 'li',
      // Links and media
      'a', 'img',
      // Block elements
      'div', 'span', 'blockquote', 'pre', 'code', 'hr',
      // Tables
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      // Figures
      'figure', 'figcaption',
    ],
    ALLOWED_ATTR: [
      'href', 'target', 'rel', 'src', 'alt', 'title', 'width', 'height',
      'class', 'id', 'style',
    ],
    // Force links to open safely
    ADD_ATTR: ['target'],
  });
}

/** Default Tailwind prose classes for consistent rich text styling */
const DEFAULT_PROSE_CLASSES =
  'prose prose-sm max-w-none dark:prose-invert ' +
  'prose-headings:text-zinc-900 dark:prose-headings:text-zinc-100 ' +
  'prose-p:text-zinc-700 dark:prose-p:text-zinc-300 ' +
  'prose-strong:text-zinc-900 dark:prose-strong:text-zinc-100 ' +
  'prose-em:text-zinc-700 dark:prose-em:text-zinc-300 ' +
  'prose-ul:text-zinc-700 dark:prose-ul:text-zinc-300 ' +
  'prose-ol:text-zinc-700 dark:prose-ol:text-zinc-300 ' +
  'prose-li:text-zinc-700 dark:prose-li:text-zinc-300 ' +
  'prose-a:text-teal-600 dark:prose-a:text-teal-400 ' +
  'prose-code:bg-zinc-100 dark:prose-code:bg-zinc-800 ' +
  'prose-code:px-1 prose-code:py-0.5 prose-code:rounded';

/**
 * MarkdownContent component renders rich text content (HTML or Markdown)
 * with Tailwind prose classes for consistent styling.
 *
 * Automatically detects whether content is HTML (from WordPress WYSIWYG)
 * or Markdown and renders accordingly. HTML content is sanitized with
 * DOMPurify to prevent XSS attacks.
 */
export const MarkdownContent = forwardRef<HTMLDivElement, MarkdownContentProps>(
  ({ content, className = '', testId }, ref) => {
    // Handle empty content
    if (!content || content.trim().length === 0) {
      return null;
    }

    // Normalize <br /> tags to newlines before HTML detection.
    // Content stored as "- Item<br /><br />\n- Item2" (markdown list with HTML line breaks)
    // should be rendered as Markdown, not HTML. Strip <br> first so containsHtmlTags
    // only returns true when there are real HTML tags (<p>, <strong>, etc.).
    const normalizedContent = useMemo(() => {
      return content.replace(/<br\s*\/?>/gi, '\n');
    }, [content]);

    const isHtml = useMemo(() => containsHtmlTags(normalizedContent), [normalizedContent]);

    const sanitizedHtml = useMemo(() => {
      if (isHtml) {
        return sanitizeHtml(normalizedContent);
      }
      return '';
    }, [normalizedContent, isHtml]);

    return (
      <div
        ref={ref}
        className={`${DEFAULT_PROSE_CLASSES} ${className}`.trim()}
        data-testid={testId}
      >
        {isHtml ? (
          <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
        ) : (
          <ReactMarkdown>{normalizedContent}</ReactMarkdown>
        )}
      </div>
    );
  }
);

MarkdownContent.displayName = 'MarkdownContent';

export { containsHtmlTags, sanitizeHtml };

export default MarkdownContent;
